import { useState } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  CardBody, 
  CardHeader, 
  Button,
  Table,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import { AddThreadUserDto, UpdatePermissionDto } from '../api/api-client';
import { useThreadUsers, useAddUserToThread, useUpdateUserPermission, useRemoveUserFromThread } from '../hooks/useThreadPermissions';
import { useAccountMembers } from '../hooks/useAccount';
import confirm from './ConfirmModal';
import { useAuth } from '../contexts/AuthContext';
import { CryptoHelper } from '../utils/cryptoHelper';

interface ThreadPermissionsProps {
  threadId: number;
  threadTitle: string;
  contentKey: CryptoKey;
  onClose: () => void;
}

export const ThreadPermissions = ({ threadId, threadTitle, contentKey, onClose }: ThreadPermissionsProps) => {
  const { user } = useAuth();
  const { data: users = [], isLoading: loading } = useThreadUsers(threadId);
  const { data: allMembers = [] } = useAccountMembers();
  const addUserMutation = useAddUserToThread();
  const updatePermissionMutation = useUpdateUserPermission();
  const removeUserMutation = useRemoveUserFromThread();
  
  // Add user modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [newUserPermission, setNewUserPermission] = useState('Read');
  
  // Combined saving state from all mutations
  const saving = addUserMutation.isPending || updatePermissionMutation.isPending || removeUserMutation.isPending;
  
  // Filter out users who already have access to this thread AND the current user
  const accountMembers = allMembers.filter(member => 
    member.id !== user?.id && // Exclude current user
    !users.some(user => user.userId === member.id) // Exclude users who already have access
  );
  
  // Available permissions
  const permissions = [
    { value: 'Read', label: 'Read', description: 'Can view the thread' },
    { value: 'Write', label: 'Write', description: 'Can view and edit the thread' }
  ];


  const handleAddUser = async () => {
    if (!selectedUserId) return;
    
    const selectedMember = accountMembers.find(m => m.id === selectedUserId);
    if (!selectedMember?.email) return;
    
    // Check if the selected user has a public key
    if (!selectedMember.publicKey) {
      throw new Error('Selected user does not have a public key configured. They must complete setup first.');
    }
    
    // Import the user's public key
    const recipientPublicKey = await CryptoHelper.importPublicKey(selectedMember.publicKey);
    
    // Encrypt the content key with the recipient's public key
    const encryptedContentKey = await CryptoHelper.encryptContentKey(contentKey, recipientPublicKey);
    
    const addMemberDto: AddThreadUserDto = {
      email: selectedMember.email,
      permission: newUserPermission,
      encryptedContentKey: encryptedContentKey
    };
    
    await addUserMutation.mutateAsync({ threadId, addMemberDto });
    
    // Reset form and close modal
    setSelectedUserId(null);
    setNewUserPermission('Read');
    setShowAddModal(false);
  };

  const handleUpdatePermission = async (userId: number, userEmail: string, newPermission: string) => {
    const updateDto: UpdatePermissionDto = {
      permission: newPermission
    };
    await updatePermissionMutation.mutateAsync({ threadId, userId, updateDto });
  };

  const handleRemoveUser = async (userId: number, userEmail: string) => {
    const confirmed = await confirm({
      title: 'Remove Access',
      message: `Are you sure you want to remove ${userEmail}'s access to this thread?`,
      confirmText: 'Remove',
      confirmColor: 'danger'
    });

    if (!confirmed) return;

    await removeUserMutation.mutateAsync({ threadId, userId });
  };

  const getPermissionBadgeColor = (permission: string) => {
    switch (permission.toLowerCase()) {
      case 'owner': return 'primary';
      case 'write': return 'success';
      case 'read': return 'info';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner color="primary" />
          <p className="mt-2">Loading thread permissions...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row>
        <Col md={10} lg={8} className="mx-auto">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h3>Thread Permissions</h3>
              <p className="text-muted mb-0">{threadTitle}</p>
            </div>
            <div>
              <Button 
                color="primary" 
                onClick={() => setShowAddModal(true)}
                disabled={saving}
                className="me-2"
              >
                <i className="fas fa-plus me-2"></i>
                Add User
              </Button>
              <Button 
                color="secondary" 
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          </div>
          
          <Card>
            <CardHeader>
              <h5 className="mb-0">Users with Access ({users.length})</h5>
            </CardHeader>
            <CardBody>
              {users.length > 0 ? (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Permission</th>
                      <th>Added</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.userId}>
                        <td>{user.email}</td>
                        <td>
                          <Badge color={getPermissionBadgeColor(user.permission!)}>
                            {user.permission}
                          </Badge>
                        </td>
                        <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}</td>
                        <td>
                          {user.permission !== 'Owner' ? (
                            <>
                              <UncontrolledDropdown className="d-inline-block me-2">
                                <DropdownToggle 
                                  caret 
                                  size="sm" 
                                  color="outline-secondary"
                                  disabled={saving}
                                >
                                  Change Permission
                                </DropdownToggle>
                                <DropdownMenu>
                                  {permissions.map((permission) => (
                                    <DropdownItem
                                      key={permission.value}
                                      onClick={() => handleUpdatePermission(user.userId!, user.email!, permission.value)}
                                      disabled={permission.value === user.permission}
                                    >
                                      <div>
                                        <strong>{permission.label}</strong>
                                        <br />
                                        <small className="text-muted">{permission.description}</small>
                                      </div>
                                    </DropdownItem>
                                  ))}
                                </DropdownMenu>
                              </UncontrolledDropdown>
                              
                              <Button 
                                size="sm" 
                                color="outline-danger"
                                onClick={() => handleRemoveUser(user.userId!, user.email!)}
                                disabled={saving}
                              >
                                Remove
                              </Button>
                            </>
                          ) : (
                            <Badge color="primary">Thread Owner</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center py-4">
                  <i className="fas fa-users fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No additional users</h5>
                  <p className="text-muted">Add users to collaborate on this thread.</p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add User Modal */}
      <Modal isOpen={showAddModal} toggle={() => {
        setShowAddModal(false);
        setSelectedUserId(null);
      }}>
        <ModalHeader toggle={() => {
          setShowAddModal(false);
          setSelectedUserId(null);
        }}>
          Add User to Thread
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={(e) => { e.preventDefault(); handleAddUser(); }}>
            <FormGroup>
              <Label for="userSelect">Select User</Label>
              {accountMembers.length > 0 ? (
                <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {accountMembers.map((member) => (
                    <div
                      key={member.id}
                      className={`d-flex align-items-center p-2 rounded mb-1 ${
                        selectedUserId === member.id ? 'bg-primary text-white' : ''
                      }`}
                      onClick={() => setSelectedUserId(member.id!)}
                      style={{ 
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        ...(selectedUserId !== member.id && {
                          ':hover': { backgroundColor: '#f8f9fa' }
                        })
                      }}
                      onMouseEnter={(e) => {
                        if (selectedUserId !== member.id) {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedUserId !== member.id) {
                          e.currentTarget.style.backgroundColor = '';
                        }
                      }}
                    >
                      <div className="flex-grow-1">
                        <div className="fw-bold">{member.email}</div>
                        <small className={selectedUserId === member.id ? 'text-white-50' : 'text-muted'}>
                          {member.accountRole} {!member.hasCompletedSetup && '(Setup pending)'}
                        </small>
                      </div>
                      {selectedUserId === member.id && (
                        <i className="fas fa-check-circle"></i>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-3 text-muted">
                  <i className="fas fa-users fa-2x mb-2"></i>
                  <p className="mb-0">No additional users available to add</p>
                  <small>All account members already have access to this thread</small>
                </div>
              )}
            </FormGroup>
            
            <FormGroup>
              <Label for="userPermission">Permission Level</Label>
              <Input
                type="select"
                id="userPermission"
                value={newUserPermission}
                onChange={(e) => setNewUserPermission(e.target.value)}
                disabled={saving}
              >
                {permissions.map((permission) => (
                  <option key={permission.value} value={permission.value}>
                    {permission.label} - {permission.description}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button 
            color="secondary" 
            onClick={() => {
              setShowAddModal(false);
              setSelectedUserId(null);
            }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button 
            color="primary" 
            onClick={handleAddUser}
            disabled={saving || !selectedUserId}
          >
            {saving ? <Spinner size="sm" className="me-2" /> : null}
            Add User
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};